/**
 * client/profile.ts
 * Responsabilidad: Gestión del perfil local del jugador (estadísticas persistentes).
 *
 * Persistencia: localStorage bajo la clave "alchem_profile".
 * Versionado: campo "version" en PlayerProfile para migraciones futuras.
 * Batching: el flag isDirty evita escribir en cada frame; save() solo cuando es necesario.
 */

import type { PlayerProfile } from "./types";
import { safeGetStorage, safeSetStorage } from "./utils";

const PROFILE_KEY = "alchem_profile";
const CURRENT_VERSION = 1;

/** Perfil por defecto para jugadores nuevos */
function createDefaultProfile(): PlayerProfile {
    return {
        version: CURRENT_VERSION,
        gamesPlayed: 0,
        totalKills: 0,
        totalDeaths: 0,
        bestMass: 0,
        bestRank: 0,
        totalTimeMs: 0,
        currentStreak: 0,
        bestStreak: 0,
        country: "Unknown",
        elementUsage: {},
        lastUpdated: Date.now(),
    };
}

/**
 * Carga el perfil de localStorage con saneamiento defensivo.
 * Si falta un campo o el JSON está corrupto, se rellena con defaults.
 */
function loadProfile(): PlayerProfile {
    const raw = safeGetStorage(PROFILE_KEY);
    if (!raw) return createDefaultProfile();
    try {
        const parsed = JSON.parse(raw) as Partial<PlayerProfile>;
        const defaults = createDefaultProfile();
        return {
            version: typeof parsed.version === "number" ? parsed.version : defaults.version,
            gamesPlayed: typeof parsed.gamesPlayed === "number" && !isNaN(parsed.gamesPlayed) ? parsed.gamesPlayed : defaults.gamesPlayed,
            totalKills: typeof parsed.totalKills === "number" && !isNaN(parsed.totalKills) ? parsed.totalKills : defaults.totalKills,
            totalDeaths: typeof parsed.totalDeaths === "number" && !isNaN(parsed.totalDeaths) ? parsed.totalDeaths : defaults.totalDeaths,
            bestMass: typeof parsed.bestMass === "number" && !isNaN(parsed.bestMass) ? parsed.bestMass : defaults.bestMass,
            bestRank: typeof parsed.bestRank === "number" && !isNaN(parsed.bestRank) ? parsed.bestRank : defaults.bestRank,
            totalTimeMs: typeof parsed.totalTimeMs === "number" && !isNaN(parsed.totalTimeMs) ? parsed.totalTimeMs : defaults.totalTimeMs,
            currentStreak: typeof parsed.currentStreak === "number" && !isNaN(parsed.currentStreak) ? parsed.currentStreak : defaults.currentStreak,
            bestStreak: typeof parsed.bestStreak === "number" && !isNaN(parsed.bestStreak) ? parsed.bestStreak : defaults.bestStreak,
            country: typeof parsed.country === "string" ? parsed.country : defaults.country,
            elementUsage: (parsed.elementUsage && typeof parsed.elementUsage === "object") ? parsed.elementUsage : defaults.elementUsage,
            lastUpdated: typeof parsed.lastUpdated === "number" ? parsed.lastUpdated : defaults.lastUpdated,
        };
    } catch {
        // JSON corrupto: resetear a defaults
        return createDefaultProfile();
    }
}

/**
 * Gestor del perfil. Instancia única (singleton) exportada al final del archivo.
 */
export class ProfileManager {
    private profile: PlayerProfile;
    private isDirty: boolean = false;
    private sessionBestMass: number = 0;
    private sessionBestStreak: number = 0;

    constructor() {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) {
            // Al iniciar sin token, el perfil se resetea a valores por defecto
            this.profile = createDefaultProfile();
            safeSetStorage(PROFILE_KEY, JSON.stringify(this.profile));
        } else {
            this.profile = loadProfile();
        }
    }

    /** Lectura de solo lectura del perfil */
    public getProfile(): Readonly<PlayerProfile> {
        return this.profile;
    }

    /** Registra partidas jugadas y muertes. Resetea la racha. */
    public recordDeath(): void {
        this.profile.gamesPlayed += 1;
        this.profile.totalDeaths += 1;
        this.profile.currentStreak = 0;
        this.isDirty = true;
    }

    /** Registra kills. Actualiza la racha actual y la mejor racha. */
    public recordKill(delta: number = 1): void {
        if (delta <= 0) return;
        this.profile.totalKills += delta;
        this.profile.currentStreak += delta;
        if (this.profile.currentStreak > this.profile.bestStreak) {
            this.profile.bestStreak = this.profile.currentStreak;
        }
        if (this.profile.currentStreak > this.sessionBestStreak) {
            this.sessionBestStreak = this.profile.currentStreak;
        }
        this.isDirty = true;
    }

    /** Registra la masa actual si supera el récord. */
    public recordMass(mass: number): void {
        const rounded = Math.round(mass);
        if (rounded > this.profile.bestMass) {
            this.profile.bestMass = rounded;
            this.isDirty = true;
        }
        if (rounded > this.sessionBestMass) {
            this.sessionBestMass = rounded;
        }
    }

    /** Registra el mejor ranking alcanzado (1..5). 0 = sin clasificar. */
    public recordRank(rank: number): void {
        if (rank <= 0) return;
        if (this.profile.bestRank === 0 || rank < this.profile.bestRank) {
            this.profile.bestRank = rank;
            this.isDirty = true;
        }
    }

    /** Acumula tiempo jugado y uso por elemento. */
    public recordPlayTime(deltaMs: number, element: string): void {
        if (deltaMs <= 0) return;
        this.profile.totalTimeMs += deltaMs;
        this.profile.elementUsage[element] = (this.profile.elementUsage[element] || 0) + deltaMs;
        this.isDirty = true;
    }

    /** Devuelve el elemento con más milisegundos acumulados. */
    public getFavoriteElement(): string {
        const usage = this.profile.elementUsage;
        let best = "";
        let bestMs = 0;
        for (const key of Object.keys(usage)) {
            if (usage[key] > bestMs) {
                bestMs = usage[key];
                best = key;
            }
        }
        return best;
    }

    /** Guarda en localStorage si hay cambios pendientes (o forzado). */
    public save(force: boolean = false): void {
        if (!force && !this.isDirty) return;
        this.profile.lastUpdated = Date.now();
        try {
            safeSetStorage(PROFILE_KEY, JSON.stringify(this.profile));
            this.isDirty = false;
        } catch {
            // Falla silenciosa si localStorage está bloqueado
        }
    }

    /** Hidrata las estadísticas locales con datos provenientes de la base de datos de Supabase. */
    public hydrate(dbProfile: Record<string, any>): void {
        if (!dbProfile || typeof dbProfile !== "object") return;
        if (typeof dbProfile.totalGames === "number") this.profile.gamesPlayed = dbProfile.totalGames;
        else if (typeof dbProfile.gamesPlayed === "number") this.profile.gamesPlayed = dbProfile.gamesPlayed;

        if (typeof dbProfile.totalKills === "number") this.profile.totalKills = dbProfile.totalKills;
        if (typeof dbProfile.totalDeaths === "number") this.profile.totalDeaths = dbProfile.totalDeaths;
        if (typeof dbProfile.bestMass === "number") this.profile.bestMass = dbProfile.bestMass;
        if (typeof dbProfile.bestRank === "number") this.profile.bestRank = dbProfile.bestRank;
        if (typeof dbProfile.totalTimeMs === "number") this.profile.totalTimeMs = dbProfile.totalTimeMs;
        if (typeof dbProfile.currentStreak === "number") this.profile.currentStreak = dbProfile.currentStreak;
        if (typeof dbProfile.bestStreak === "number") this.profile.bestStreak = dbProfile.bestStreak;
        if (typeof dbProfile.country === "string") this.profile.country = dbProfile.country;

        this.save(true);
    }

    /** Sincroniza el perfil actual a Supabase a través del endpoint /api/profile. */
    public async syncToDatabase(extraData: { alias?: string; cosmetics?: any; country?: string } = {}): Promise<void> {
        const token = localStorage.getItem("alchem_auth_token") || localStorage.getItem("colyseus-auth-token");
        if (!token) return;

        try {
            const payload = {
                totalGames: this.profile.gamesPlayed,
                totalKills: this.profile.totalKills,
                totalDeaths: this.profile.totalDeaths,
                bestMass: this.profile.bestMass,
                bestRank: this.profile.bestRank,
                totalTimeMs: this.profile.totalTimeMs,
                currentStreak: this.profile.currentStreak,
                bestStreak: this.profile.bestStreak,
                sessionBestMass: this.sessionBestMass,
                sessionBestStreak: this.sessionBestStreak,
                ...extraData,
            };

            const res = await fetch("/api/profile", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                this.sessionBestMass = 0;
                this.sessionBestStreak = 0;
            }
        } catch (e) {
            console.warn("[Profile] Error al sincronizar con la base de datos:", e);
        }
    }

    /** Resetea el perfil a valores por defecto y guarda. */
    public resetProfile(): void {
        this.profile = createDefaultProfile();
        this.save(true);
    }
}

/** Instancia singleton del gestor de perfil */
export const profileManager = new ProfileManager();

/**
 * Formatea milisegundos a un string legible.
 * Ejemplos: "45s", "3m 12s", "1h 24m".
 */
export function formatTime(ms: number): string {
    if (ms < 60000) {
        return `${Math.floor(ms / 1000)}s`;
    }
    if (ms < 3600000) {
        const m = Math.floor(ms / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        return `${m}m ${s}s`;
    }
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
}
